import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { isEmpty } from "lodash";
import moment from "moment";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const SLICE_COLORS = [
  "#e7505a", "#f59e0b", "#8B5CF6", "#06B6D4", "#EF4444",
  "#F97316", "#6366F1", "#EC4899", "#84CC16", "#14B8A6",
];

// Prop opcional "activeOrOverloadedOnly" (projeto "layout novo"
// 2026-10-22, pedido do usuario: na aba Qualidade & carga so' quem tem
// ticket ativo agora OU ficou sobrecarregado no periodo deveria
// aparecer na "Lista de Tecnicos" - a lista inteira de usuarios so'
// poluia). Default false pra nao mudar nada do Classico, que chama
// este componente sem essa prop.
const SobrecargaChart = ({ dateStart, dateEnd, activeOrOverloadedOnly = false }) => {
  const theme = useTheme();
  // Fix 2026-10-22 (QA modo escuro do layout Novo, tarefa #66): os 3
  // cards abaixo ("Normal"/"Sobrecarregado"/"Total") tinham bgcolor
  // hardcoded pra tons pasteis claros (#e8f5e9 etc) - no modo escuro
  // ficava um retangulo claro ilegivel no meio da tela escura, com o
  // texto (que segue theme.palette.text, branco no escuro) quase
  // invisivel sobre o fundo claro. alpha() tinge a cor real do status
  // sobre QUALQUER fundo, sempre legivel nos dois modos - nao e'
  // exclusivo do layout Novo, o Dashboard Classico (aba Qualidade &
  // carga) tambem usa este componente e tinha o mesmo problema se
  // alguem ativasse o escuro por la.
  const isDark = theme.palette.mode === "dark";
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);

  useEffect(() => {
    fetchSobrecarga();
  }, [dateStart, dateEnd]);

  async function fetchSobrecarga() {
    setLoading(true);
    try {
      const params = {};
      if (!isEmpty(dateStart) && moment(dateStart).isValid()) {
        params.dataInicial = moment(dateStart).format("YYYY-MM-DD");
      }
      if (!isEmpty(dateEnd) && moment(dateEnd).isValid()) {
        params.dataFinal = moment(dateEnd).format("YYYY-MM-DD");
      }
      const response = await api.get("/dashboard/tecnicos-sobrecarga", { params });
      setData(response.data.data || []);
      setTecnicos(response.data.tecnicos || []);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar dados de sobrecarga");
    }
    setLoading(false);
  }

  const getValueByStatus = (status) => {
    const item = data.find(d => d.status === status);
    return item ? parseInt(item.quantidade) : 0;
  };

  const normal = getValueByStatus("Normal");
  const sobrecarregado = getValueByStatus("Sobrecarregado");
  const total = normal + sobrecarregado;

  const historicoData = [...tecnicos]
    .filter(t => (t.vezes_sobrecarregado || 0) > 0)
    .sort((a, b) => (b.vezes_sobrecarregado || 0) - (a.vezes_sobrecarregado || 0))
    .map((t, i) => ({ name: t.name, vezes: t.vezes_sobrecarregado || 0, color: SLICE_COLORS[i % SLICE_COLORS.length] }));

  // So' entra na tabela quem tem ticket ativo agora OU ficou
  // sobrecarregado alguma vez no periodo - o resto (tecnico sem nenhum
  // atendimento nem historico de sobrecarga) so' teria linha vazia.
  const tecnicosParaTabela = activeOrOverloadedOnly
    ? tecnicos.filter(t => (t.tickets_ativos || 0) > 0 || (t.vezes_sobrecarregado || 0) > 0)
    : tecnicos;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Sobrecarga dos Técnicos
      </Typography>

      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 6px rgba(0,0,0,0.05)", mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Quantas vezes cada atendente ficou sobrecarregado no período
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Conta cada momento em que um atendente aceitou um ticket já estando no limite (ou acima) de atendimentos simultâneos.
          </Typography>
          {historicoData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Nenhum atendente ficou sobrecarregado no período selecionado.
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={historicoData}
                  dataKey="vezes"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  labelLine={false}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {historicoData.map((d) => (
                    <Cell key={d.name} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value} vez(es)`, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 2, bgcolor: alpha("#26c281", isDark ? 0.16 : 0.12) }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">Normal (agora)</Typography>
              <Typography variant="h3" fontWeight="bold" color="#26c281">{normal}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 2, bgcolor: alpha("#e7505a", isDark ? 0.18 : 0.12) }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">Sobrecarregado (agora)</Typography>
              <Typography variant="h3" fontWeight="bold" color="#e7505a">{sobrecarregado}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 2, bgcolor: isDark ? alpha("#ffffff", 0.06) : "#f5f5f5" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">Total de Técnicos</Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary">{total}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabela de Técnicos */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Lista de Técnicos
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: isDark ? alpha("#ffffff", 0.06) : "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Técnico</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">Tickets Ativos (agora)</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">Limite Máx.</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">Vezes sobrecarregado (período)</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">Status (agora)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tecnicosParaTabela.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="text.secondary">
                            {activeOrOverloadedOnly ? "Nenhum técnico ativo ou sobrecarregado no período" : "Nenhum técnico encontrado"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tecnicosParaTabela.map((tec) => (
                        <TableRow key={tec.id} sx={{
                          bgcolor: tec.status === 'Sobrecarregado' ? alpha("#e7505a", isDark ? 0.14 : 0.06) : 'inherit',
                          '&:hover': { bgcolor: isDark ? alpha("#ffffff", 0.06) : '#fafafa' }
                        }}>
                          <TableCell>
                            <Typography fontWeight={tec.status === 'Sobrecarregado' ? 'bold' : 'normal'}>
                              {tec.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{tec.tickets_ativos}</TableCell>
                          <TableCell align="center">{tec.limite_maximo_simultaneo}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={tec.vezes_sobrecarregado || 0}
                              size="small"
                              color={(tec.vezes_sobrecarregado || 0) > 0 ? "error" : "default"}
                              variant={(tec.vezes_sobrecarregado || 0) > 0 ? "filled" : "outlined"}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={tec.status}
                              size="small"
                              color={tec.status === 'Sobrecarregado' ? 'error' : 'success'}
                              variant="filled"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SobrecargaChart;
